import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle, Crown, Ticket, Trophy } from '@phosphor-icons/react';

const STORAGE_KEY = 'onboardingSeen.v1';

const STEPS = [
  {
    icon: Crown,
    title: 'Welcome to the 1:2000 Club',
    body: 'The most exclusive lottery on the planet. Only 2,000 tickets per draw — never one more.'
  },
  {
    icon: Ticket,
    title: 'Buy one ticket. Only one.',
    body: 'Each player gets exactly one ticket per draw. €150 each. When the pool fills, the draw locks and the wheel spins.'
  },
  {
    icon: Trophy,
    title: '€200,000 + 20 × €1,000',
    body: 'One winner takes home €200,000. Twenty more take home €1,000 each. Odds: 1 in 2,000 to win.'
  }
];

const WelcomeOnboardingModal = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user || user.role === 'admin') return;
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        setOpen(true);
      }
    } catch (storageReadErr) {
      console.debug('Onboarding flag read failed (private mode?):', storageReadErr);
    }
  }, [user]);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (storageWriteErr) {
      console.debug('Onboarding flag save failed:', storageWriteErr);
    }
  };

  const next = () => {
    if (step >= STEPS.length - 1) {
      close();
    } else {
      setStep(step + 1);
    }
  };

  const skip = () => close();

  if (!open) return null;
  const Step = STEPS[step];
  const Icon = Step.icon;
  const isLast = step >= STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent
        data-testid="welcome-onboarding-modal"
        className="bg-[#0e0b04] border border-[#cba135]/20 backdrop-blur-2xl max-w-md text-center"
      >
        <DialogHeader>
          <DialogTitle className="sr-only">{Step.title}</DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
            className="px-2 pt-2 pb-4"
          >
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-[#cba135] to-[#a8872b] flex items-center justify-center mb-5 shadow-[0_0_24px_rgba(203,161,53,0.35)]">
              <Icon size={32} weight="duotone" className="text-[#0e0b04]" />
            </div>
            <h3 className="heading-font text-2xl text-[#cba135] mb-3 leading-tight">{Step.title}</h3>
            <p className="text-base text-[#cba135]/75 leading-relaxed">{Step.body}</p>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-center gap-1.5 mt-2 mb-4">
          {STEPS.map((s, i) => (
            <span
              key={s.title}
              className={`h-1.5 rounded-full transition-all ${i === step ? 'bg-[#cba135] w-6' : 'bg-[#cba135]/25 w-1.5'}`}
            />
          ))}
        </div>

        <div className="flex gap-3 px-2">
          {!isLast && (
            <Button
              data-testid="onboarding-skip"
              variant="ghost"
              onClick={skip}
              className="flex-1 text-[#cba135]/60 hover:text-[#cba135] hover:bg-[#cba135]/5"
            >
              Skip
            </Button>
          )}
          <Button
            data-testid="onboarding-next"
            onClick={next}
            className="flex-1 bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold"
          >
            {isLast ? (
              <>
                <CheckCircle size={18} weight="fill" className="mr-2" />
                Let's go
              </>
            ) : (
              'Next'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeOnboardingModal;
